/**
 * PATH: packages/ui/src/kanban/zLayers.ts
 * FILE_ID: UI-KANBAN-ZLAYERS-001
 * PURPOSE: THE tokenised z-index ladder for every kanban overlay
 *          surface (context menus, drawers, scrims, dialogs).
 * WHY: UAT P1 "menu opens in the wrong reference position / behind
 *      other chrome" (docs/uat/KANBAN_UAT_SCOPE.md). The overlays
 *      grew an ad-hoc ladder: the card context menu sat at z-50
 *      while the lane/board/canvas menus sat at z-[100] and the
 *      card drawer at z-60 — so right-clicking a card with the
 *      drawer open rendered the menu BEHIND the drawer, and the
 *      card menu always lost to the lane menu. One module owns the
 *      ordering so a new surface picks a token instead of inventing
 *      a literal.
 * FLOW:
 *   ┌──────────────┐   ┌──────────────┐   ┌─────────────────────┐
 *   │ overlay kind │ → │ Z_LAYER token│ → │ style={{ zIndex }}  │
 *   └──────────────┘   └──────────────┘   └─────────────────────┘
 * LADDER (bottom → top):
 *   boardChrome(30) < drawerOverlay(55) < drawer(60)
 *     < menuScrim(90) < menu(100) < popover(110) < dialog(120)
 *   Menus sit ABOVE drawers: a context menu is always the most
 *   recent, most transient intent — it must never be occluded by
 *   the split-screen/drawer it was opened over.
 * RULES:
 *   - Use `style={{ zIndex: Z_LAYER.x }}` (Tailwind JIT cannot see
 *     template-literal classes, so arbitrary `z-[${x}]` is banned).
 *   - Never introduce a raw z literal in kanban overlay code — add
 *     a token here instead, in ladder order.
 * RELATED:
 *   - components/KanbanContextMenu.tsx (card menu — menu/menuScrim)
 *   - components/KanbanLaneContextMenu.tsx / KanbanBoardContextMenu.tsx
 *   - components/KanbanCanvasContextMenu.tsx / ChecklistItemContextMenu.tsx
 *   - components/CardDetailDrawer.tsx (drawer/drawerOverlay)
 */

export const Z_LAYER = {
  /** Sticky in-board chrome (filter bar, lane headers). */
  boardChrome: 30,
  /** Dim/click-away overlay behind the card drawer. */
  drawerOverlay: 55,
  /** The card detail drawer / split-screen shell. */
  drawer: 60,
  /** Mobile bottom-sheet scrim behind a context menu. */
  menuScrim: 90,
  /** Every portaled context menu (card, lane, board, canvas,
   *  checklist). Above the drawer by design — see file header. */
  menu: 100,
  /** Popovers SPAWNED from a menu (lane colour / members / inline
   *  prompt) — must beat the menu that opened them. */
  popover: 110,
  /** Blocking dialogs (create board, cascade-archive confirm). */
  dialog: 120,
} as const

export type ZLayerToken = keyof typeof Z_LAYER
